// ABOUTME: Displays the submitted cancellation request result in the order details panel.
// ABOUTME: Supports processing, completed, rejected, and polling-timeout states.
import {Button} from '@hm/fundamental'
import React from 'react'
import {useTranslation} from 'react-i18next'

import ReviewIllustration from '../../../../../Assets/orderChangeResult/review.svg'
import SuccessIllustration from '../../../../../Assets/orderChangeResult/success.svg'
import {CancellationResultType} from '../../types/cancellationRequest.types'

interface OrderCancellationResultProps {
  resultType: CancellationResultType
  isPolling: boolean
  onViewOrderDetails: () => void
  onClose: () => void
}

export const OrderCancellationResult: React.FC<OrderCancellationResultProps> = ({
  resultType,
  isPolling,
  onViewOrderDetails,
  onClose
}) => {
  const {t} = useTranslation()
  const isSuccess = resultType === 'success'
  const isFailure = resultType === 'failure'

  const title = isSuccess
    ? t('orderIntake.cancellationResult.successTitle', 'Order cancellation completed')
    : isFailure
      ? t('orderIntake.cancellationResult.failureTitle', 'Order cancellation was not approved')
      : t('orderIntake.cancellationResult.pendingTitle', 'Cancellation request submitted')

  const description = isSuccess
    ? t(
        'orderIntake.cancellationResult.successDescription',
        'Return to the order details and refresh to see the updated order.'
      )
    : isFailure
      ? t(
          'orderIntake.cancellationResult.failureDescription',
          'Please contact dispatch if you need help with this cancellation.'
        )
      : t(
          'orderIntake.cancellationResult.pendingDescription',
          'Your cancellation is being processed. Please check back later.'
        )

  return (
    <section className="flex h-full flex-col p-6" data-test-id="order-cancellation-result">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">
          {t('orderIntake.cancellationResult.header', 'Order cancellation')}
        </h2>
        <Button variant="secondary" size="small" onClick={onClose}>
          {t('close', 'Close')}
        </Button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
        <img
          src={isSuccess ? SuccessIllustration : ReviewIllustration}
          alt=""
          className="h-48 w-48"
        />
        <div className="flex max-w-sm flex-col gap-2">
          <h3 className="text-2xl font-bold">{title}</h3>
          <p className="text-sm text-neutral-600">{description}</p>
          {isPolling && (
            <p className="text-sm font-semibold" role="status">
              {t('orderIntake.cancellationResult.checkingStatus', 'Checking request status...')}
            </p>
          )}
        </div>
      </div>

      <Button variant="primary" size="large" onClick={onViewOrderDetails} className="w-full">
        {t('orderIntake.cancellationResult.viewOrderDetails', 'View order details')}
      </Button>
    </section>
  )
}
